import React, { useRef, forwardRef, useImperativeHandle, useEffect } from "react";
import { Card, Typography, Box, useTheme } from "@mui/material";
import ReactPlayer from "react-player";

const VideoPlayerCard = forwardRef(({
  interview,
  videoBlobUrl,
  questionText,
  transcriptText,
  autoPlayKey,
  onVideoPlay,
}, ref) => {
  const theme = useTheme();
  const playerRef = useRef(null);

  useImperativeHandle(ref, () => ({
    pause: () => {
      const internalPlayer = playerRef.current?.getInternalPlayer?.();
      if (internalPlayer) {
        internalPlayer.pause();
      }
    },
    getVideoElement: () => playerRef.current?.getInternalPlayer?.(),
  }));

  const hasVideo =
    videoBlobUrl &&
    typeof videoBlobUrl === "string" &&
    videoBlobUrl.trim() !== "";

  const hasQuestion =
    questionText &&
    typeof questionText === "string" &&
    questionText.trim() !== "";

  const hasTranscript =
    transcriptText &&
    typeof transcriptText === "string" &&
    transcriptText.trim() !== "";


      useEffect(() => {
    if (!hasVideo || !autoPlayKey) return;
    const videoEl = playerRef.current;
    if (!videoEl) return;

    const playPromise = videoEl.play();
    if (playPromise && typeof playPromise.then === "function") {
      playPromise.catch((err) => {
        // eslint-disable-next-line no-console
        console.debug("Video autoplay blocked or failed:", err);
      });
    }
  }, [autoPlayKey, hasVideo]);
  return (
    <Card
      elevation={0}
      sx={{
        overflow: "hidden",
        backgroundColor: theme.palette.background.paper,
        border: `1px solid ${theme.palette.divider}`,
        boxShadow:
          theme.palette.mode === "dark"
            ? "0px 1px 3px rgba(0, 0, 0, 0.3)"
            : "0px 1px 3px rgba(0, 0, 0, 0.1)",
        borderRadius: 3,
      }}
    >
      {hasVideo ? (
        <Box
          sx={{
            position: "relative",
            width: "100%",
            paddingTop: "56.25%",
            backgroundColor: "#000",
          }}
        >
          <Box
            sx={{
              position: "absolute",
              top: 0, left: 0,
              width: "100%", height: "100%",
            }}
          >
            <ReactPlayer
              ref={playerRef}
              url={videoBlobUrl}
              src={videoBlobUrl}
              key={videoBlobUrl}
              controls
              playsInline

              width="100%"
              height="100%"
              download={true}             
                
              config={{
                file: {
                  attributes: {
                    crossOrigin: "anonymous",
                    preload: "auto",
                    controlsList: "download",
                  },
                },
              }}
              onPlay={() => {
                if (typeof onVideoPlay === "function") onVideoPlay();
              }}
            />
          </Box>
        </Box>
      ) : (
        <Box
          sx={{
            aspectRatio: "16/9",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor:
              theme.palette.mode === "dark"
                ? "rgba(255,255,255,0.05)"
                : "#f3f4f6",
          }}
        >
          <Typography variant="body2" color="text.secondary">
            No Recording Preview Available.
          </Typography>
        </Box>
      )}

      {hasQuestion && (
        <Box
          sx={{
            p: 2,
            borderTop: `1px solid ${theme.palette.divider}`,
            backgroundColor:
              theme.palette.mode === "dark"
                ? "rgba(0,0,0,0.2)"
                : "rgba(0,0,0,0.02)",
          }}
        >
          <Typography
            variant="body2"
            fontWeight={700}
            sx={{ display: "block", mb: 0.5 }}
          >
            Question
          </Typography>
          <Typography
            variant="caption"
            sx={{
              color: theme.palette.text.primary,
              minHeight: "2em",
              overflowWrap: "anywhere",
            }}
          >
            {questionText}
          </Typography>
        </Box>
      )}

      {hasTranscript && (
        <Box
          sx={{
            p: 2,
            pt: 1,
            borderTop: `1px solid ${theme.palette.divider}`,
            backgroundColor:
              theme.palette.mode === "dark"
                ? "rgba(0,0,0,0.3)"
                : "rgba(0,0,0,0.01)",
          }}
        >
          <Typography
            variant="body2"
            fontWeight="bold"
            sx={{ display: "block", mb: 0.5 }}
          >
            Transcript
          </Typography>
          <Typography
            variant="caption"
            sx={{
              color: theme.palette.text.secondary,
              maxHeight: 200,
              overflowY: "auto",
              whiteSpace: "pre-wrap",
            }}
          >
            {transcriptText}
          </Typography>
        </Box>
      )}
    </Card>
  );
});

VideoPlayerCard.displayName = "VideoPlayerCard";
export default VideoPlayerCard;